import { createAsyncThunk, createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import type { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { entityAgreementApi } from "legal-entities/agreements/_api/entity-agreement-api-service";
import { EntityAgreementJSON } from "legal-entities/agreements/_schema/entity-agreement-schema";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import _ from "lodash";
import Session from "users/__session/session";

type EntityAgreementStoreState = {
  byId: {
    entries: Record<string, EntityAgreementJSON>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

const initialState: EntityAgreementStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
};

export const populateEntityAgreement = createAsyncThunk(
  "entityAgreements/getEntityAgreementById",
  async ({ id }: { session: Session; id: Guid }, thunkAPI) => {
    const request = thunkAPI.dispatch(
      entityAgreementApi.endpoints.getEntityAgreementById.initiate({ id }, { forceRefetch: true }),
    );
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await request.unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  },
);

export const populateEntityAgreements = createAsyncThunk(
  "entityAgreements/getEntityAgreementsByIds",
  async ({ entityAgreementIds }: { session: Session; entityAgreementIds: Guid[] }, thunkAPI) => {
    if (!entityAgreementIds.length) return [];

    const store = thunkAPI.getState() as RootState;
    const missingEntityAgreementIds = _.uniqWith(
      entityAgreementIds.filter((entityAgreementId) => !store.legalEntityAgreements.byId.entries[entityAgreementId.value]),
      (a, b) => a.isEqualTo(b),
    );
    if (!missingEntityAgreementIds.length) return [];

    const request = thunkAPI.dispatch(
      entityAgreementApi.endpoints.getEntityAgreementsByIds.initiate(
        { entityAgreementIds: missingEntityAgreementIds },
        { forceRefetch: true },
      ),
    );
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await request.unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  },
);

export const entityAgreementsSlice = createSlice({
  name: "entityAgreements",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(entityAgreementApi.endpoints.getEntityAgreementById.matchPending, (state, action) => {
      const id = action.meta.arg.originalArgs.id.value;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });
    builder.addMatcher(entityAgreementApi.endpoints.getEntityAgreementById.matchFulfilled, (state, action) => {
      const entityAgreement = action.payload;
      state.byId.entries[entityAgreement.id.value] = entityAgreement.toJSON();
      state.byId.loading[entityAgreement.id.value] = false;
      state.byId.error[entityAgreement.id.value] = null;
    });
    builder.addMatcher(entityAgreementApi.endpoints.getEntityAgreementById.matchRejected, (state, action) => {
      const id = action.meta.arg.originalArgs.id.value;
      state.byId.loading[id] = false;
      state.byId.error[id] = toSerializedError(action.payload ?? action.error);
    });
    builder.addMatcher(entityAgreementApi.endpoints.getEntityAgreementsByIds.matchPending, (state, action) => {
      for (const entityAgreementId of action.meta.arg.originalArgs.entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = true;
        state.byId.error[entityAgreementId.value] = null;
      }
    });
    builder.addMatcher(entityAgreementApi.endpoints.getEntityAgreementsByIds.matchFulfilled, (state, action) => {
      for (const entityAgreement of action.payload) {
        state.byId.entries[entityAgreement.id.value] = entityAgreement.toJSON();
      }

      for (const entityAgreementId of action.meta.arg.originalArgs.entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = false;
      }
    });
    builder.addMatcher(entityAgreementApi.endpoints.getEntityAgreementsByIds.matchRejected, (state, action) => {
      const error = toSerializedError(action.payload ?? action.error);
      for (const entityAgreementId of action.meta.arg.originalArgs.entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = false;
        state.byId.error[entityAgreementId.value] = error;
      }
    });
    builder.addMatcher(entityAgreementApi.endpoints.updateEntityAgreement.matchFulfilled, (state, action) => {
      const entityAgreement = action.payload;
      state.byId.entries[entityAgreement.id.value] = entityAgreement.toJSON();
      state.byId.loading[entityAgreement.id.value] = false;
      state.byId.error[entityAgreement.id.value] = null;
    });
  },
});

export const getEntityAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return;
  const json = state.legalEntityAgreements?.byId.entries[id.value];
  return EntityAgreement.fromJSON(json);
};

export const getIsLoadingEntityAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return false;
  return state.legalEntityAgreements?.byId.loading[id.value] ?? false;
};

export const getErrorLoadingEntityAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return undefined;
  return state.legalEntityAgreements?.byId.error[id.value];
};

const selectEntityAgreementsByIds = createSelector(
  [(state: RootState) => state.legalEntityAgreements.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids
      .map((id) => EntityAgreement.fromJSON(entries[id.value]))
      .filter((entityAgreement) => entityAgreement !== undefined);
  },
);
export const getEntityAgreementsByIds = (ids: Guid[]) => (state: RootState) => selectEntityAgreementsByIds(state, ids);

const selectLoadingEntityAgreementIds = createSelector(
  [(state: RootState) => state.legalEntityAgreements.byId.loading],
  (loadingMap) => {
    return Object.keys(loadingMap)
      .filter((id) => loadingMap[id])
      .map((id) => Guid.fromJSON(id))
      .filter((id): id is Guid => Boolean(id));
  },
);
export const getLoadingEntityAgreementIds = (state: RootState) => selectLoadingEntityAgreementIds(state);

export const getIsLoadingEntityAgreementByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some((id) => state.legalEntityAgreements.byId.loading[id.value]);

export const getAreErrorsLoadingEntityAgreementsByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some((id) => Boolean(state.legalEntityAgreements.byId.error[id.value]));

export type EntityAgreementError = {
  id: Guid;
  error: SerializedError;
};

const selectAllEntityAgreementErrors = createSelector(
  [(state: RootState) => state.legalEntityAgreements.byId.error],
  (errors) => {
    return Object.keys(errors)
      .map((id) => {
        const error = errors[id];
        const entityAgreementId = Guid.fromJSON(id);
        if (!entityAgreementId || !error) return null;
        return { id: entityAgreementId, error };
      })
      .filter((record): record is EntityAgreementError => record !== null);
  },
);
export const getAllEntityAgreementErrors = (state: RootState) => selectAllEntityAgreementErrors(state);

function toSerializedError(error: unknown): SerializedError {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
  if (error && typeof error === "object") {
    const record = error as { name?: unknown; message?: unknown; stack?: unknown };
    return {
      name: typeof record.name === "string" ? record.name : undefined,
      message: typeof record.message === "string" ? record.message : "Error fetching entity agreement info.",
      stack: typeof record.stack === "string" ? record.stack : undefined,
    };
  }
  return { message: "Error fetching entity agreement info." };
}
